import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { copyTextToClipboard } from "@/lib/copy-text";

type ExpandableCopyDetailsProps = {
  copyText: string;
  aiInputText?: string | null;
  aiOutputText?: string | null;
  copyLabel?: string;
  emptyInputText?: string;
  emptyOutputText?: string;
};

function DetailPanel({
  title,
  text,
  emptyText,
}: {
  title: string;
  text?: string | null;
  emptyText: string;
}) {
  const value = text?.trim();
  return (
    <div className="rounded-md border border-border/70 bg-muted/40 p-2">
      <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      <div className="max-h-40 overflow-y-auto whitespace-pre-wrap break-words rounded-sm bg-background/80 p-2 font-mono text-[11px] text-foreground">
        {value || <span className="font-sans italic text-muted-foreground">{emptyText}</span>}
      </div>
    </div>
  );
}

export function ExpandableCopyDetails({
  copyText,
  aiInputText,
  aiOutputText,
  copyLabel = "Click to copy",
  emptyInputText = "No chat input was captured for this step.",
  emptyOutputText = "No AI output was captured for this step.",
}: ExpandableCopyDetailsProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mt-1">
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        <button
          type="button"
          className="inline-flex h-5 w-5 items-center justify-center rounded-sm hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-expanded={expanded}
          aria-label={expanded ? "Collapse AI details" : "Expand AI details"}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setExpanded((value) => !value);
          }}
        >
          <ChevronRight className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-90" : ""}`} />
        </button>
        <button
          type="button"
          className="rounded-sm hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            const ok = await copyTextToClipboard(copyText);
            if (ok) toast.success("Copied to clipboard", { duration: 2000 });
            else toast.error("Could not copy to clipboard");
          }}
        >
          {copyLabel}
        </button>
      </div>
      {expanded ? (
        <div className="mt-2 grid gap-2">
          <DetailPanel title="AI Input" text={aiInputText} emptyText={emptyInputText} />
          <DetailPanel title="AI Output" text={aiOutputText} emptyText={emptyOutputText} />
        </div>
      ) : null}
    </div>
  );
}
